// src/pages/AttendancePage.js
import React, { useState, useEffect, useCallback } from "react";
import { classAPI, attendanceAPI, yearAPI } from "../api/api";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./AttendancePage.css";

export default function AttendancePage() {
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]); // NEW: dynamic groups from API
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState("");
  const [yearInfo, setYearInfo] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [entries, setEntries] = useState({});
  const [suppEntries, setSuppEntries] = useState({});
  const [suppItems, setSuppItems] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showSundayPopup, setShowSundayPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const hdr = { Authorization: `Bearer ${token}` };

    classAPI.list().then((r) => {
      const cls = r.data.data || [];
      cls.sort((a, b) => parseInt(a.class_number) - parseInt(b.class_number));
      setClasses(cls);
      const init = {};
      cls.forEach((c) => { init[c.id] = { total: 0, meal_served: 1 }; });
      setEntries(init);
    }).catch(() => toast.error("ತರಗತಿ ಲೋಡ್ ಆಗಲಿಲ್ಲ"));

    // NEW: Load groups dynamically
    fetch("/api/groups", { headers: hdr })
      .then(r => r.json())
      .then(d => {
        const grps = d.data || [];
        grps.sort((a, b) => parseInt(a.display_order) - parseInt(b.display_order));
        setGroups(grps);
      })
      .catch(() => {});

    fetch("/api/supplementary/items", { headers: hdr })
      .then((r) => r.json())
      .then((d) => setSuppItems(d.data || []))
      .catch(() => {});

    yearAPI.list().then((r) => {
      const ys = r.data.data || [];
      setYears(ys);
      const cur = ys.find((y) => y.is_current === 1);
      if (cur) { setYearId(cur.id); setYearInfo(cur); }
      else if (ys.length > 0) { setYearId(ys[0].id); setYearInfo(ys[0]); }
    }).catch(() => toast.error("ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಲೋಡ್ ಆಗಲಿಲ್ಲ"));
  }, []);

  useEffect(() => {
    const found = years.find((y) => String(y.id) === String(yearId));
    if (found) setYearInfo(found);
  }, [yearId, years]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAttendanceForDate = useCallback(() => {
    if (!date || classes.length === 0) return;
    setFetching(true);
    setResult(null);
    attendanceAPI.byDate(date).then((r) => {
      const att = r.data.data?.attendance || [];
      if (att.length > 0) {
        const updated = {};
        classes.forEach((c) => {
          updated[c.id] = entries[c.id] || { total: 0, meal_served: 1 };
        });
        att.forEach((a) => {
          updated[a.class_id] = {
            total: (a.boys_present || 0) + (a.girls_present || 0),
            meal_served: a.meal_served,
          };
        });
        setEntries(updated);
        if (r.data.data?.total_students > 0) {
          setResult(r.data.data);
          const suppMap = {};
          att.forEach((a) => {
            if (a.supplementary && a.supplementary.length > 0) {
              suppMap[a.class_id] = {};
              a.supplementary.forEach((s) => {
                suppMap[a.class_id][s.item_id] = s.student_count;
              });
            }
          });
          setSuppEntries(suppMap);
          setIsEditing(true);
          toast("📝 ಈ ದಿನಾಂಕದ ಹಾಜರಾತಿ ಲೋಡ್ ಆಗಿದೆ — ಬದಲಿಸಿ ಮತ್ತೆ ಉಳಿಸಬಹುದು",
            { icon: "✏️", duration: 4000 });
        }
      } else {
        const reset = {};
        classes.forEach((c) => { reset[c.id] = { total: 0, meal_served: 1 }; });
        setEntries(reset);
        setSuppEntries({});
        setIsEditing(false);
      }
    }).catch(() => {}).finally(() => setFetching(false));
  }, [date, classes.length]);

  useEffect(() => { loadAttendanceForDate(); }, [loadAttendanceForDate]);

  const updateTotal = (classId, val) => {
    const num = Math.max(0, parseInt(val) || 0);
    setEntries((p) => ({ ...p, [classId]: { ...p[classId], total: num } }));
    setSuppEntries((p) => ({ ...p, [classId]: {} }));
  };

  const updateSupp = (classId, itemId, val) => {
    const num = Math.max(0, parseInt(val) || 0);
    const total = entries[classId]?.total || 0;
    if (suppItems.length === 2) {
      const otherItem = suppItems.find(i => i.id !== itemId);
      setSuppEntries((p) => ({
        ...p,
        [classId]: {
          ...p[classId],
          [itemId]: num,
          [otherItem.id]: Math.max(0, total - num),
        },
      }));
      return;
    }
    setSuppEntries((p) => ({
      ...p,
      [classId]: { ...p[classId], [itemId]: num },
    }));
  };

  const totalStudents = Object.values(entries).reduce(
    (s, e) => s + (e.total || 0), 0,
  );

  const doSave = async () => {
    setLoading(true);
    const tid = toast.loading("ಉಳಿಸುತ್ತಿದೆ...");
    try {
      const payload = {
        attendance_date: date,
        academic_year_id: yearId,
        remarks,
        attendance: classes.map((c) => {
          const total = entries[c.id]?.total || 0;
          const suppArr = suppItems
            .map((si) => ({
              item_id: si.id,
              student_count: parseInt(suppEntries[c.id]?.[si.id] || 0),
            }))
            .filter((s) => s.student_count > 0);
          return {
            class_id:      c.id,
            boys_present:  total,
            girls_present: 0,
            meal_served:   entries[c.id]?.meal_served ?? 1,
            supplementary: suppArr,
          };
        }),
      };
      const { data } = await attendanceAPI.save(payload);
      toast.dismiss(tid);
      setResult(data.data);
      toast.success(
        isEditing ? "ಹಾಜರಾತಿ ತಿದ್ದುಪಡಿ ✅" : "ಹಾಜರಾತಿ ಉಳಿಸಲಾಗಿದೆ ✅",
        { duration: 4000 }
      );
      setIsEditing(true);
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e.response?.data?.message || "ಉಳಿಸುವುದು ವಿಫಲ");
    } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!yearId) { toast.error("ಮೊದಲು ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಆಯ್ಕೆ ಮಾಡಿ"); return; }
    if (totalStudents === 0) { toast.error("ಕನಿಷ್ಠ ಒಂದು ಮಗುವಿನ ಹಾಜರಾತಿ ನಮೂದಿಸಿ"); return; }
    for (const cls of classes) {
      const total = entries[cls.id]?.total || 0;
      if (!entries[cls.id]?.meal_served || total === 0) continue;
      const suppSum = suppItems.reduce(
        (s, si) => s + (parseInt(suppEntries[cls.id]?.[si.id] || 0)), 0
      );
      if (suppSum > total) {
        toast.error(`${cls.name_kannada}: ಹೆಚ್ಚುವರಿ ಆಹಾರ (${suppSum}) ಒಟ್ಟು ಮಕ್ಕಳು (${total}) ಗಿಂತ ಹೆಚ್ಚಿದೆ`);
        return;
      }
    }
    const dayOfWeek = new Date(date).getDay();
    if (dayOfWeek === 0) { setShowSundayPopup(true); return; }
    await doSave();
  };

  // NEW: Build dynamic group sections from API groups
  // Each group shows its assigned classes sorted by class_number
  const groupSections = groups.map((g, idx) => ({
    id:      g.id,
    label:   g.name_kannada,
    color:   idx % 2 === 0 ? "green" : "blue",
    classes: classes
      .filter(c => String(c.group_id) === String(g.id))
      .sort((a, b) => parseInt(a.class_number) - parseInt(b.class_number)),
  })).filter(g => g.classes.length > 0);

  // Unassigned classes fallback
  const unassignedClasses = classes.filter(c => !c.group_id);
  if (unassignedClasses.length > 0) {
    groupSections.push({
      id: "none", label: "⚠️ ಗುಂಪು ನಿಯೋಜಿಸಿಲ್ಲ", color: "grey",
      classes: unassignedClasses,
    });
  }

  const hasNoYears = years.length === 0;
  const hasNoCurrent = years.length > 0 && !years.find((y) => y.is_current === 1);

  return (
    <div className="att-page">
      <div className="page-header">
        <h1 className="page-title">✅ ದೈನಂದಿನ ಹಾಜರಾತಿ</h1>
        <p className="page-desc">ಪ್ರತಿ ತರಗತಿಯ ಒಟ್ಟು ಮಕ್ಕಳು ಮತ್ತು ಹೆಚ್ಚುವರಿ ಆಹಾರ ನಮೂದಿಸಿ</p>
      </div>

      {hasNoYears && (
        <div className="att-warning-box">
          <span className="warn-icon">⚠️</span>
          <div className="warn-content">
            <div className="warn-title">ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಮಾಡಿಲ್ಲ!</div>
            <div className="warn-desc">ಹಾಜರಾತಿ ನಮೂದಿಸಲು ಮೊದಲು ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಮಾಡಬೇಕು.</div>
          </div>
          <button className="warn-action-btn" onClick={() => navigate("/academic-year")}>
            📅 ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಮಾಡಿ →
          </button>
        </div>
      )}

      {hasNoCurrent && (
        <div className="att-info-box">
          <span>ℹ️</span>
          <span>ಯಾವ ಪ್ರಸ್ತುತ ವರ್ಷ ನಿಗದಿ ಮಾಡಿಲ್ಲ — </span>
          <button className="inline-link" onClick={() => navigate("/academic-year")}>
            ಇಲ್ಲಿ ನಿಗದಿ ಮಾಡಿ →
          </button>
        </div>
      )}

      <div className="att-controls">
        <div className="control-group">
          <label className="ctrl-label">📅 ದಿನಾಂಕ</label>
          <input type="date" className="ctrl-input" value={date}
            onChange={(e) => setDate(e.target.value)}
            max={new Date().toISOString().slice(0, 10)} />
        </div>
        <div className="control-group">
          <label className="ctrl-label">🎓 ಶೈಕ್ಷಣಿಕ ವರ್ಷ</label>
          {years.length === 0 ? (
            <div className="no-year-ctrl">
              <span>ವರ್ಷ ಇಲ್ಲ</span>
              <button className="no-year-btn" onClick={() => navigate("/academic-year")}>+ ಮಾಡಿ</button>
            </div>
          ) : (
            <select className="ctrl-input year-select" value={yearId}
              onChange={(e) => setYearId(e.target.value)}>
              <option value="">— ಆಯ್ಕೆ ಮಾಡಿ —</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.year_label}{y.is_current === 1 ? " ✅ (ಪ್ರಸ್ತುತ)" : ""}
                </option>
              ))}
            </select>
          )}
        </div>
        {yearInfo && (
          <div className="year-info-pill">
            <span>📅</span>
            <div>
              <div className="yip-year">{yearInfo.year_label}</div>
              <div className="yip-dates">
                {new Date(yearInfo.start_date).toLocaleDateString("kn-IN", { day: "numeric", month: "short" })}
                {" — "}
                {new Date(yearInfo.end_date).toLocaleDateString("kn-IN", { day: "numeric", month: "short", year: "numeric" })}
              </div>
            </div>
            {yearInfo.is_current === 1 && <span className="yip-current">ಪ್ರಸ್ತುತ</span>}
          </div>
        )}
        <div className="att-total-badge">
          <span className="total-num">{totalStudents}</span>
          <span className="total-lbl">ಒಟ್ಟು ಮಕ್ಕಳು</span>
        </div>
      </div>

      {fetching && (
        <div className="att-loading">
          <span className="spinner-sm" style={{ marginRight: "0.5rem" }} />
          ಲೋಡ್ ಆಗುತ್ತಿದೆ...
        </div>
      )}

      {/* Dynamic group sections */}
      {!hasNoYears && groupSections.map((group) => (
        <div key={group.id} className="att-group-section">
          <h3 className={`group-heading group-${group.color}`}>{group.label}</h3>
          <div className="att-table-wrap">
            <table className="att-table">
              <thead>
                <tr>
                  <th>ತರಗತಿ</th>
                  <th>ಒಟ್ಟು ಮಕ್ಕಳು</th>
                  {suppItems.map((si) => (
                    <th key={si.id}>{si.name_kannada}</th>
                  ))}
                  <th>ಊಟ</th>
                </tr>
              </thead>
              <tbody>
                {group.classes.map((cls) => {
                  const e = entries[cls.id] || { total: 0, meal_served: 1 };
                  const total = e.total || 0;
                  const mealOn = !!e.meal_served;
                  const suppSum = suppItems.reduce(
                    (s, si) => s + (parseInt(suppEntries[cls.id]?.[si.id] || 0)), 0
                  );
                  return (
                    <tr key={cls.id} className={total > 0 ? "row-filled" : ""}>
                      <td className="cls-name-cell">
                        <span className={`cls-badge cls-badge-${group.color}`}>{cls.class_number}</span>
                        {cls.name_kannada}
                      </td>
                      <td>
                        <input
                          className="num-input attendance-input"
                          type="number" min="0" max="999"
                          value={e.total === 0 ? "" : e.total}
                          onChange={(ev) => updateTotal(cls.id, ev.target.value)}
                          placeholder="0"
                          style={{ width: 70, textAlign: "center" }}
                          onKeyDown={(ev) => {
                            if (ev.key === "Enter") {
                              ev.preventDefault();
                              const row = ev.target.closest("tr");
                              const suppInput = row.querySelector(".supp-input");
                              if (suppInput) suppInput.focus();
                            }
                          }}
                        />
                      </td>
                      {suppItems.map((si) => {
                        const val = parseInt(suppEntries[cls.id]?.[si.id] || 0);
                        return (
                          <td key={si.id}>
                            {mealOn && total > 0 ? (
                              <input
                                className="num-input supp-input"
                                type="number" min="0" max={total}
                                value={val === 0 ? "" : val}
                                placeholder="0"
                                onChange={(ev) => updateSupp(cls.id, si.id, ev.target.value)}
                                onKeyDown={(ev) => {
                                  if (ev.key === "Enter") {
                                    ev.preventDefault();
                                    const currentRow = ev.target.closest("tr");
                                    const nextRow = currentRow?.nextElementSibling;
                                    if (!nextRow) return;
                                    const nextAtt = nextRow.querySelector(".attendance-input");
                                    if (nextAtt) nextAtt.focus();
                                  }
                                }}
                                style={{ width: 70, textAlign: "center" }}
                              />
                            ) : (
                              <span style={{ color: "#4b5563", fontSize: 12 }}>—</span>
                            )}
                          </td>
                        );
                      })}
                      <td>
                        <label className="meal-toggle">
                          <input type="checkbox" checked={mealOn}
                            onChange={(ev) => setEntries((p) => ({
                              ...p,
                              [cls.id]: { ...p[cls.id], meal_served: ev.target.checked ? 1 : 0 },
                            }))} />
                          <span className="toggle-track"><span className="toggle-thumb" /></span>
                          <span className="toggle-lbl">{mealOn ? "ಹೌದು" : "ಇಲ್ಲ"}</span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
                <tr className="group-total-row">
                  <td><strong>ಗುಂಪು ಒಟ್ಟು</strong></td>
                  <td>
                    <strong className="grand-total-num">
                      {group.classes.reduce((s, c) => s + (entries[c.id]?.total || 0), 0)}
                    </strong>
                  </td>
                  {suppItems.map((si) => (
                    <td key={si.id}>
                      <strong style={{ color: "#4ade80" }}>
                        {group.classes.reduce(
                          (s, c) => s + (parseInt(suppEntries[c.id]?.[si.id] || 0)), 0
                        ) || "—"}
                      </strong>
                    </td>
                  ))}
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {!hasNoYears && (
        <div className="att-remarks">
          <label className="ctrl-label">📝 ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ)</label>
          <textarea className="ctrl-input remarks-ta" rows={2}
            placeholder="ಇಂದಿನ ವಿಶೇಷ ಟಿಪ್ಪಣಿ..."
            value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
      )}

      {isEditing && (
        <div style={{
          background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.25)",
          borderRadius: "12px", padding: "0.7rem 1.1rem", marginBottom: "0.8rem",
          display: "flex", alignItems: "center", gap: "0.7rem",
        }}>
          <span style={{ fontSize: "1.2rem" }}>✏️</span>
          <div>
            <div style={{ fontFamily: "Noto Sans Kannada", fontSize: "0.82rem", fontWeight: 700, color: "#93c5fd" }}>
              ತಿದ್ದುಪಡಿ ಮೋಡ್ — ಈ ದಿನಾಂಕದ ಹಾಜರಾತಿ ಲೋಡ್ ಆಗಿದೆ
            </div>
            <div style={{ fontFamily: "Noto Sans Kannada", fontSize: "0.73rem", color: "rgba(147,197,253,0.65)" }}>
              ಸಂಖ್ಯೆ ಸರಿ ಮಾಡಿ "ಹಾಜರಾತಿ ಉಳಿಸಿ" ಒತ್ತಿ
            </div>
          </div>
        </div>
      )}

      {!hasNoYears && (
        <button className={`att-submit-btn ${loading ? "loading" : ""}`}
          onClick={handleSubmit} disabled={loading || !yearId}
          style={{ opacity: !yearId ? 0.6 : 1 }}>
          {loading ? (
            <><span className="spinner" />ಉಳಿಸುತ್ತಿದೆ...</>
          ) : !yearId ? (
            "⚠️ ಮೊದಲು ಶೈಕ್ಷಣಿಕ ವರ್ಷ ಆಯ್ಕೆ ಮಾಡಿ"
          ) : (
            "💾 ಹಾಜರಾತಿ ಉಳಿಸಿ"
          )}
        </button>
      )}

      {result && <AttendanceResult result={result} yearInfo={yearInfo} date={date} />}

      {showSundayPopup && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
        }}>
          <div style={{
            background: "#1a2332", border: "1px solid rgba(251,191,36,0.4)",
            borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%",
            textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "0.8rem" }}>📅</div>
            <div style={{ fontFamily: "Noto Sans Kannada", fontSize: "1rem", fontWeight: 700, color: "#fde047", marginBottom: "0.5rem" }}>
              ಇಂದು ಭಾನುವಾರ (ರಜೆ ದಿನ)
            </div>
            <div style={{ fontFamily: "Noto Sans Kannada", fontSize: "0.88rem", color: "rgba(255,255,255,0.7)", marginBottom: "1.5rem", lineHeight: 1.6 }}>
              Attendance ದಾಖಲಿಸಬೇಕೇ?
            </div>
            <div style={{ display: "flex", gap: "0.8rem" }}>
              <button onClick={() => { setShowSundayPopup(false); doSave(); }} style={{
                flex: 1, padding: "0.7rem", borderRadius: "10px",
                background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.4)",
                color: "#4ade80", fontFamily: "Noto Sans Kannada", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer",
              }}>✅ ಹೌದು</button>
              <button onClick={() => setShowSundayPopup(false)} style={{
                flex: 1, padding: "0.7rem", borderRadius: "10px",
                background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)",
                color: "#f87171", fontFamily: "Noto Sans Kannada", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer",
              }}>❌ ಇಲ್ಲ</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AttendanceResult({ result, yearInfo, date }) {
  const itemMap = {};
  (result.consumption_summary || []).forEach((r) => {
    if (!itemMap[r.item_name])
      itemMap[r.item_name] = { total_qty: 0, unit: r.display_unit, total_cost: 0 };
    itemMap[r.item_name].total_qty += r.display_qty || 0;
    itemMap[r.item_name].total_cost += r.cost || 0;
  });
  const fmtDate = new Date(date).toLocaleDateString("kn-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  return (
    <div className="att-result">
      <div className="result-header">
        <div>
          <h2 className="result-title">📊 ಲೆಕ್ಕದ ಫಲಿತಾಂಶ</h2>
          <div className="result-meta">
            <span>📅 {fmtDate}</span>
            {yearInfo && <span className="result-year-badge">🎓 {yearInfo.year_label}</span>}
          </div>
        </div>
        <div className="result-badges">
          <span className="rbadge rbadge-green">👥 ಒಟ್ಟು ಮಕ್ಕಳು: {result.total_students}</span>
          <span className="rbadge rbadge-blue">💰 ಒಟ್ಟು ಖರ್ಚು: ₹{parseFloat(result.total_cost || 0).toFixed(2)}</span>
        </div>
      </div>
      <div className="result-items">
        {Object.entries(itemMap).map(([name, info]) => (
          <div key={name} className="result-item-row">
            <span className="ri-name">{name}</span>
            <span className="ri-qty">
              {info.unit === "count" ? `${info.total_qty} ಸಂಖ್ಯೆ`
                : info.unit === "Rs" ? `₹${info.total_qty.toFixed(2)}`
                : `${info.total_qty.toFixed(3)} ${info.unit}`}
            </span>
            <span className="ri-cost">₹{info.total_cost.toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}